"use client";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import {
  projects,
  cameraWaypoints,
  floatingLabels,
  connectionLines,
} from "./constants";
import { interpolatePath } from "./cameraPath";
import ProjectCard, { MobileProjectCard } from "./ProjectCard";
import FloatingLabel from "~/components/shared/FloatingLabel";
import ScrollReveal from "~/components/shared/ScrollReveal";
import useCursorProximity from "~/hooks/useCursorProximity";
import type { HighlightData } from "~/types/highlight";
import SectionTitleCard from "~/components/SectionTitleCard";
import type { CrosshairData } from "~/components/Crosshair";
import type { XAxisData } from "~/components/XAxisTicks";
import type { MouseOffset } from "~/components/GridTicks";
import { FOCUS_SNAP } from "~/utils/scatterTransforms";

const MOBILE_BREAKPOINT = 768;

export default function ProjectSection({
  highlightRef,
  crosshairRef,
  xAxisRef,
  mouseOffsetRef,
}: {
  highlightRef: RefObject<HighlightData>;
  crosshairRef: RefObject<CrosshairData>;
  xAxisRef: RefObject<XAxisData>;
  mouseOffsetRef: RefObject<MouseOffset>;
}) {
  const sectionRef = useRef<HTMLElement>(null);
  const labelsRef = useRef<HTMLDivElement>(null);
  const mouseLayerRef = useRef<HTMLDivElement>(null);
  const worldRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const svgLinesRef = useRef<SVGSVGElement>(null);
  const progressRef = useRef(0);
  const sectionInViewRef = useRef(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [landedBits, setLandedBits] = useState(0);
  const [viewport, setViewport] = useState({ w: 0, h: 0 });

  // Refs that the mouse-follow rAF loop reads — updated by scroll handler, zero re-renders from mouse
  const focusIntensityRef = useRef(0);
  const mouseNxRef = useRef(0);
  const mouseNyRef = useRef(0);

  useEffect(() => {
    const updateViewport = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useCursorProximity(labelsRef);

  // Scroll-driven updates — imperative for continuous values, React state only for activeIndex
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    let scrollRaf = 0;

    const handleScroll = () => {
      cancelAnimationFrame(scrollRaf);
      scrollRaf = requestAnimationFrame(() => {
        const rect = section.getBoundingClientRect();
        const sectionHeight = section.offsetHeight;
        const vw = window.innerWidth;
        const viewportH = window.innerHeight;
        const scrolled = -rect.top;
        const scrollableDistance = sectionHeight - viewportH;
        const raw = scrollableDistance <= 0 ? 1 : scrolled / scrollableDistance;
        const progress = Math.min(Math.max(raw, 0), 1);
        progressRef.current = progress;

        const midScreen = viewportH * 0.5;
        const inView = rect.top < midScreen && rect.bottom > midScreen;
        sectionInViewRef.current = inView;

        // Compute camera + world transform
        const camera = interpolatePath(cameraWaypoints, progress);
        const tx = -camera.x + vw / 2;
        const ty = -camera.y + viewportH / 2;

        // Imperative world transform
        if (worldRef.current) {
          worldRef.current.style.transform = `translate(${tx}px, ${ty}px)`;
          if (vw === 0) {
            worldRef.current.style.opacity = "0";
          } else {
            worldRef.current.style.opacity = "";
          }
        }

        // Imperative progress bar
        if (progressBarRef.current) {
          progressBarRef.current.style.transform = `translateX(-50%) scaleY(${progress})`;
        }

        // Imperative SVG connection line updates
        if (svgLinesRef.current) {
          const groups = svgLinesRef.current.children;
          const n = cameraWaypoints.length - 1;
          for (let i = 0; i < connectionLines.length; i++) {
            const [a, b] = connectionLines[i];
            const pa = projects[a].position;
            const pb = projects[b].position;
            const midX = (pa.x + pb.x) / 2;
            const midY = (pa.y + pb.y) / 2;
            const dist = Math.sqrt(
              (midX - camera.x) ** 2 + (midY - camera.y) ** 2,
            );
            const baseOpacity = Math.max(0, 1 - dist / 900) * 0.4;

            const dx = pb.x - pa.x;
            const dy = pb.y - pa.y;
            const lineLen = Math.sqrt(dx * dx + dy * dy);

            const startIdx = Math.min(a, b);
            const endIdx = Math.max(a, b);
            const lineStartProgress = startIdx / n;
            const lineEndProgress = endIdx / n;
            const lineRange = lineEndProgress - lineStartProgress;

            let fillFraction = 0;
            if (lineRange > 0) {
              fillFraction = Math.max(
                0,
                Math.min(1, (progress - lineStartProgress) / lineRange),
              );
            }

            const filledLen = fillFraction * lineLen;
            const unfilledLen = lineLen - filledLen;

            const g = groups[i];
            if (!g) continue;
            const bgLine = g.children[0] as SVGLineElement | undefined;
            const fillLine = g.children[1] as SVGLineElement | undefined;

            if (bgLine) {
              bgLine.style.opacity = String(baseOpacity);
            }
            if (fillLine) {
              fillLine.setAttribute(
                "stroke-dasharray",
                `${filledLen} ${unfilledLen}`,
              );
              fillLine.style.opacity = String(Math.max(baseOpacity, 0.6));
            }
          }
        }

        // Compute active index — only trigger React re-render when it changes
        const last = projects.length - 1;
        const dotFloat = progress * last;
        const newActiveIndex = Math.max(
          0,
          Math.min(last, Math.round(dotFloat)),
        );
        setActiveIndex((prev) =>
          prev !== newActiveIndex ? newActiveIndex : prev,
        );

        // Trigger re-render when any card's landed/scattered state flips
        let bits = 0;
        for (let i = 0; i < projects.length; i++) {
          const pdx = projects[i].position.x - camera.x;
          const pdy = projects[i].position.y - camera.y;
          const pDist = Math.sqrt(pdx * pdx + pdy * pdy);
          const pFocus = inView ? Math.max(0, 1 - pDist / 600) : 0;
          if (pFocus > FOCUS_SNAP) bits |= 1 << i;
        }
        setLandedBits((prev) => (prev !== bits ? bits : prev));

        // Update external refs (imperative, no re-renders)
        const isMobileView = vw > 0 && vw < MOBILE_BREAKPOINT;
        const activeProject = projects[newActiveIndex];
        const activeDx = activeProject.position.x - camera.x;
        const activeDy = activeProject.position.y - camera.y;
        const activeDist = Math.sqrt(activeDx * activeDx + activeDy * activeDy);
        const focusIntensity = inView ? Math.max(0, 1 - activeDist / 400) : 0;
        focusIntensityRef.current = focusIntensity;

        if (!isMobileView && inView) {
          highlightRef.current = {
            text: activeProject.title,
            intensity: focusIntensity,
            owner: "projects",
          };
          crosshairRef.current = {
            label: `${Math.round(camera.x)}, ${Math.round(window.scrollY)}`,
            focused: focusIntensity > 0.8,
            visible: true,
            owner: "projects",
          };
          xAxisRef.current = {
            cameraX: camera.x,
            translateX: tx,
            visible: true,
          };
        } else if (
          !isMobileView &&
          !inView &&
          crosshairRef.current.owner === "projects"
        ) {
          highlightRef.current = { text: "", intensity: 0 };
          crosshairRef.current = {
            label: "",
            focused: false,
            visible: false,
          };
          xAxisRef.current = { cameraX: 0, translateX: 0, visible: false };
        }
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(scrollRaf);
    };
  }, [highlightRef, crosshairRef, xAxisRef]);

  // ── Mouse-follow camera offset (desktop only) ──
  // Self-stopping rAF loop — reads refs, zero React re-renders from mouse moves.
  // Applies a subtle translate to the mouse-layer wrapper that sits inside the
  // scroll-driven world container. Stops when offset settles, restarts on pointer move.
  const onWorldPointerMove = useCallback((e: PointerEvent) => {
    mouseNxRef.current = (e.clientX / window.innerWidth - 0.5) * 2;
    mouseNyRef.current = (e.clientY / window.innerHeight - 0.5) * 2;
  }, []);

  useEffect(() => {
    if (viewport.w < MOBILE_BREAKPOINT) return;

    const MAX_OFFSET = 140; // px max camera shift per axis
    const LERP = 0.06; // smoothing factor — lower = more floaty
    let offsetX = 0;
    let offsetY = 0;
    let raf = 0;

    let settled = false;

    function tick() {
      const el = mouseLayerRef.current;
      if (!el) {
        raf = requestAnimationFrame(tick);
        return;
      }

      const intensity = focusIntensityRef.current;
      const targetX = mouseNxRef.current * MAX_OFFSET * intensity;
      const targetY = mouseNyRef.current * MAX_OFFSET * intensity;

      offsetX += (targetX - offsetX) * LERP;
      offsetY += (targetY - offsetY) * LERP;

      // Snap to zero when negligible to avoid sub-pixel jitter
      if (
        Math.abs(offsetX) < 0.05 &&
        Math.abs(offsetY) < 0.05 &&
        intensity === 0
      ) {
        offsetX = 0;
        offsetY = 0;
        if (!settled) {
          el.style.transform = "translate(0px, 0px)";
          mouseOffsetRef.current.x = 0;
          mouseOffsetRef.current.y = 0;
          settled = true;
        }
        // Stop the loop when fully settled
        raf = 0;
        return;
      }

      settled = false;

      // Camera follows mouse → world shifts opposite direction
      el.style.transform = `translate(${-offsetX}px, ${-offsetY}px)`;

      // Write mouse offset for axis tick containers (applied as CSS transforms)
      mouseOffsetRef.current.x = offsetX;
      mouseOffsetRef.current.y = offsetY;

      raf = requestAnimationFrame(tick);
    }

    // Restart the loop on pointer move if it settled
    const onMove = (e: PointerEvent) => {
      onWorldPointerMove(e);
      if (!raf) raf = requestAnimationFrame(tick);
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("pointermove", onMove);
      cancelAnimationFrame(raf);
    };
  }, [viewport.w, onWorldPointerMove, mouseOffsetRef]);

  // Derive render values from latest progress + activeIndex
  // landedBits triggers re-renders when any card's landed state flips — read refs while fresh
  void landedBits;
  const progress = progressRef.current;
  const sectionInView = sectionInViewRef.current;

  const camera = interpolatePath(cameraWaypoints, progress);
  const tx = -camera.x + viewport.w / 2;
  const ty = -camera.y + viewport.h / 2;

  // Scale factor so fragment offsets shrink proportionally with the polaroid
  // Polaroid uses w-[min(760px,60vw)] → scale = min(1, vw * 0.6 / 760)
  const cardScale = viewport.w > 0 ? Math.min(1, (viewport.w * 0.6) / 760) : 1;
  // ── Render both layouts; CSS picks the right one (zero layout shift) ──
  return (
    <section
      ref={sectionRef}
      id="work"
      aria-label="Projects"
      className="relative scroll-mt-14"
    >
      {/* ── Mobile / Reduced-motion: vertical stack ──
          Visible on < md, or on md+ when user prefers reduced motion */}
      <div className="px-4 py-20 md:motion-safe:hidden">
        <div className="mb-12 flex justify-center">
          <SectionTitleCard title="Projects" rotate={-2} tapeColor="teal" />
        </div>
        <div className="mx-auto flex max-w-[400px] flex-col gap-36 md:max-w-[500px]">
          {projects.map((project, i) => (
            <ScrollReveal key={project.title} variant="card">
              <MobileProjectCard project={project} index={i} />
            </ScrollReveal>
          ))}
        </div>
      </div>

      {/* ── Desktop: 2D panning world ──
          Hidden on < md, and hidden when user prefers reduced motion */}
      <div className="hidden h-[700vh] md:motion-safe:block">
        {/* Section title — static, scrolls away naturally */}
        <div className="flex items-center justify-center pt-24">
          <SectionTitleCard title="Projects" rotate={-2} tapeColor="teal" />
        </div>

        <div className="sticky top-0 h-svh w-full overflow-clip">
          {/* Gate on real viewport so the world doesn't flash at (0,0) before
              useEffect measures dimensions (prevents CLS from hero area) */}
          <div
            ref={worldRef}
            className="absolute will-change-transform"
            style={{
              transform: `translate(${tx}px, ${ty}px)`,
              ...(viewport.w === 0 && { opacity: 0 }),
            }}
          >
            {/* Mouse-follow parallax layer — imperatively translated by rAF loop */}
            <div ref={mouseLayerRef} className="will-change-transform">
              {/* Connection lines between related projects */}
              <svg
                ref={svgLinesRef}
                className="pointer-events-none absolute top-0 left-0"
                style={{
                  width: 2000,
                  height: 3000,
                  left: -500,
                  top: -500,
                  overflow: "visible",
                }}
              >
                {connectionLines.map(([a, b], i) => {
                  const pa = projects[a].position;
                  const pb = projects[b].position;
                  return (
                    <g key={i}>
                      {/* Background track */}
                      <line
                        x1={pa.x + 500}
                        y1={pa.y + 500}
                        x2={pb.x + 500}
                        y2={pb.y + 500}
                        stroke="currentColor"
                        strokeWidth="1"
                        className="text-border-hairline"
                        style={{ opacity: 0 }}
                      />
                      {/* Filled portion — dasharray updated imperatively by scroll handler */}
                      <line
                        x1={pa.x + 500}
                        y1={pa.y + 500}
                        x2={pb.x + 500}
                        y2={pb.y + 500}
                        stroke="var(--accent)"
                        strokeWidth="2"
                        strokeDasharray="0 9999"
                        strokeLinecap="round"
                        style={{ opacity: 0 }}
                      />
                    </g>
                  );
                })}
              </svg>

              {/* Floating labels — skills, annotations, section header */}
              <div ref={labelsRef}>
                {floatingLabels.map((label, i) => {
                  const visibility = 0.7;

                  // Deterministic per-label float parameters
                  // Round to 2dp so server HTML survives browser CSS normalisation without hydration drift
                  // This is the classic GLSL pseudo-random hash just ported into JS
                  const seed = Math.sin(i * 73.17 + 3.91) * 43758.5453;
                  const phase =
                    Math.round((seed - Math.floor(seed)) * 1000) / 100;
                  const driftDuration =
                    Math.round((6 + (i % 5) * 1.4) * 100) / 100;
                  const baseOpacity = Math.round(visibility * 0.4 * 100) / 100;

                  return (
                    <FloatingLabel
                      key={i}
                      text={label.text}
                      size={label.size}
                      baseOpacity={baseOpacity}
                      x={`${label.position.x}px`}
                      y={`${label.position.y}px`}
                      driftDuration={driftDuration}
                      driftPhase={phase}
                    />
                  );
                })}
              </div>

              {/* Project cards — always visible, no fade */}
              {projects.map((project, index) => {
                const dx = project.position.x - camera.x;
                const dy = project.position.y - camera.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                const focus = sectionInView ? Math.max(0, 1 - dist / 600) : 0;

                const cameraOffset: [number, number] = [
                  Math.max(
                    -1,
                    Math.min(1, (camera.x - project.position.x) / 600),
                  ),
                  Math.max(
                    -1,
                    Math.min(1, (camera.y - project.position.y) / 600),
                  ),
                ];

                return (
                  <div
                    key={project.title}
                    className="absolute"
                    style={{
                      left: project.position.x,
                      top: project.position.y,
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    <ProjectCard
                      project={project}
                      index={index}
                      focus={focus}
                      scale={cardScale}
                      cameraOffset={cameraOffset}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Progress track — clickable dots */}
          <div className="absolute top-1/2 right-4 flex -translate-y-1/2 flex-col items-center gap-0">
            {/* Track line (base + fill) aligned to dot centers */}
            <div className="bg-border-hairline absolute top-5 bottom-5 left-1/2 w-px -translate-x-1/2" />
            <div
              ref={progressBarRef}
              className="bg-accent absolute top-5 bottom-5 left-1/2 w-px origin-top -translate-x-1/2"
              style={{
                transform: `translateX(-50%) scaleY(${progress})`,
                transition: "transform 80ms linear",
              }}
            />

            {projects.map((_, i) => {
              const dotProgress = i / (cameraWaypoints.length - 1);
              const isCurrent = i === activeIndex;
              const isPast = i < activeIndex;

              return (
                <button
                  key={i}
                  type="button"
                  aria-label={`Go to project ${projects[i].title}`}
                  className="group outline-accent relative flex h-10 w-10 cursor-pointer items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-4"
                  onClick={() => {
                    const section = sectionRef.current;
                    if (!section) return;
                    const sectionTop = section.offsetTop;
                    const sectionHeight = section.offsetHeight;
                    const viewportH = window.innerHeight;
                    const scrollableDistance = sectionHeight - viewportH;
                    const targetScroll =
                      sectionTop + dotProgress * scrollableDistance;
                    window.scrollTo({ top: targetScroll, behavior: "smooth" });
                  }}
                >
                  <span
                    className="block rounded-full transition-all duration-200 group-hover:scale-150"
                    style={{
                      width: isCurrent ? 10 : 6,
                      height: isCurrent ? 10 : 6,
                      backgroundColor: isCurrent
                        ? "var(--accent)"
                        : isPast
                          ? "var(--accent)"
                          : "var(--border-hairline)",
                      opacity: isCurrent ? 1 : isPast ? 0.5 : 0.6,
                    }}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
